/**
 * Which QuestionnaireResponses belong in the Supervision cosign queue and whether
 * a supervisee document is awaiting supervisor co-signature.
 */
import type { Practitioner, Questionnaire, QuestionnaireResponse } from '@medplum/fhirtypes';
import { questionnaireRefIsGroupTherapySessionNote } from '@unify/fhir-model/constants';
import { APPOINTMENT_TAG_SYSTEM, practitionerRequiresCosignature } from '../../appointments/fhirNoteMapping';
import {
  isChartDocumentQuestionnaireResponse,
  isHistoricalMigrationQuestionnaireResponse,
} from '@/lib/chart/chartDocumentWorkflow';
import { chartDocumentRequiresSupervisorCosign } from '@/lib/chart/chartDocumentTemplates';
import type { DocumentReviewStatus } from '../types';

/** Fallback when the Questionnaire resource failed to load but the QR ref names a cosign template. */
export function questionnaireRefImpliesSupervisorCosign(qref: string | undefined): boolean {
  if (!qref) return false;
  const key = qref.toLowerCase();
  return (
    key.includes('diagnosis-and-treatment-plan')
    || key.includes('treatment-plan')
    || key.includes('continuing-recovery-plan')
    || key.includes('discharge-summary')
  );
}

export function chartDocumentRequiresSupervisorCosignFromQr(
  qr: QuestionnaireResponse,
  questionnaire: Questionnaire | null | undefined,
): boolean {
  if (chartDocumentRequiresSupervisorCosign(questionnaire ?? undefined)) return true;
  return isChartDocumentQuestionnaireResponse(qr) && questionnaireRefImpliesSupervisorCosign(qr.questionnaire);
}

export function hasAppointmentDocumentationTag(qr: QuestionnaireResponse): boolean {
  return qr.meta?.tag?.some((t) => t.system === APPOINTMENT_TAG_SYSTEM) ?? false;
}

/** Patient fan-out copies inherit signatures; only their Group-subject shared note is cosigned. */
export function isGroupPatientFanoutCopy(qr: QuestionnaireResponse): boolean {
  return (
    qr.subject?.reference?.startsWith('Patient/') === true
    && hasAppointmentDocumentationTag(qr)
    && questionnaireRefIsGroupTherapySessionNote(qr.questionnaire)
  );
}

/**
 * True when this QR is a valid source row for the cosign / unsigned-notes queue
 * (appointment session notes, treatment plans, discharge summaries, etc.).
 */
export function isCosignQueueSourceDocument(
  qr: QuestionnaireResponse,
  questionnaire: Questionnaire | null | undefined,
): boolean {
  if (isHistoricalMigrationQuestionnaireResponse(qr)) return false;
  if (isGroupPatientFanoutCopy(qr)) return false;
  if (hasAppointmentDocumentationTag(qr)) return true;
  if (isChartDocumentQuestionnaireResponse(qr)) {
    return chartDocumentRequiresSupervisorCosignFromQr(qr, questionnaire);
  }
  return false;
}

/**
 * True when a supervisee-authored document should appear as awaiting supervisor
 * co-signature (pre-licensed / supervised provider only).
 */
export function isAwaitingSupervisorCosign(
  qr: QuestionnaireResponse,
  questionnaire: Questionnaire | null | undefined,
  author: Practitioner | null | undefined,
  status: DocumentReviewStatus,
  options?: { authorIsKnownSupervisee?: boolean },
): boolean {
  if (status === 'edits-requested') return true;
  if (status !== 'signed') return false;
  if (!options?.authorIsKnownSupervisee && !practitionerRequiresCosignature(author ?? undefined)) {
    return false;
  }
  return isCosignQueueSourceDocument(qr, questionnaire);
}
